import React, { useState, useEffect } from "react";
import homeLogo from "../../Assets/home-main.webp";
import Home2 from "./Home2";
import Projects from "../Projects/Projects";
import About from "../About/About";
import Type from "./Type";
import Contact from "../Contact/Contact";
import ResumeNew from "../Resume/ResumeNew";
import Certifications from "../Certifications/Certifications";
import SocialPopup from "./SocialPopup";

/* ─── Data ─────────────────────────────────────────────────── */
const stats = [
  { value: "15+", label: "AI APIs Shipped" },
  { value: "3 yrs", label: "Production Exp." },
  { value: "30%", label: "Perf. Boost" },
  { value: "60+", label: "REST APIs Built" },
];

const services = [
  {
    num: "01",
    icon: "◈",
    title: "LLM & RAG Systems",
    desc: "Custom chatbots, document Q&A, semantic search using LangChain, OpenAI,Hybrid, Ollama, Pinecone, Weaviate and Chroma.",
  },
  {
    num: "02",
    icon: "◎",
    title: "FastAPI / Django Backends",
    desc: "Scalable REST APIs, microservices, Redis caching, WebSockets & real-time AI pipelines ready for production.",
  },
  {
    num: "03",
    icon: "⬡",
    title: "GCP Microservices & CI/CD",
    desc: "Cloud-native deployments, Docker, monitoring, logging, and resource-optimized backends for enterprise-grade solutions.",
  },
  {
    num: "04",
    icon: "◉",
    title: "AI API Integrations",
    desc: "Integrate OpenAI, Gemini, Qwen, Hugging Face, and custom transformer models into your existing product.",
  },
  {
    num: "05",
    icon: "◐",
    title: "Speech & NLP Systems",
    desc: "Speech-to-text, real-time audio processing, signal analysis — as seen in the AI Quran App (75% error reduction).",
  },
{
  num: "06",
  icon: "⬢",
  title: "AI Pipelines",
  desc: "LLM-powered data pipelines, AI agents and multi-agent systems with real-time intelligence — production experience from ChainGPT.org.",
},
];

/* ─── Scroll helper ─────────────────────────────────────────── */
const go = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ─── Component ─────────────────────────────────────────────── */
export default function Home() {
  const [ready, setReady]       = useState(false);
  const [mouse, setMouse]       = useState({ x: -9999, y: -9999 });
  const [hovered, setHovered]   = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 80);
    const mv = (e) => setMouse({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", mv);
    return () => { clearTimeout(t); window.removeEventListener("mousemove", mv); };
  }, []);

  return (
    <section className="portfolio-root">
      {/* ── ambient cursor glow (fixed, pointer-events: none) ── */}
      <div
        className="cursor-glow"
        style={{ left: mouse.x, top: mouse.y }}
        aria-hidden="true"
      />

      {/* ════════════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════════════ */}
      <div className="hero-section" id="home">
        {/* CSS-only animated background — zero canvas, zero click-blocking */}
        <div className="hero-bg" aria-hidden="true">
          <div className="bg-grid" />
          <div className="bg-orb bg-orb-a" />
          <div className="bg-orb bg-orb-b" />
          <div className="bg-orb bg-orb-c" />
        </div>

        <div className={`hero-inner ${ready ? "hero-in" : ""}`}>

          {/* ── Left column ── */}
          <div className="hero-left">
            <div className="avail-tag">
              <span className="avail-dot" />
              Open to new projects
            </div>

            <p className="hero-eyebrow">AI · Python · FastAPI · Django</p>

            <h1 className="hero-name">
              Shahid<br />
              <span className="name-stroke">Hussain</span>
            </h1>

<p className="hero-bio">
  I build <strong>AI chatbots, LLM-powered systems, agents, and scalable backend solutions</strong> that help businesses automate workflows, process data intelligently, and scale faster.
</p>

            <div className="hero-type">
              <span className="type-prefix">→</span>
              <Type />
            </div>

            {/* ── THE BUTTONS — pure <a> tags, no canvas can block these ── */}
            <div className="hero-actions">
              <a
                href="#contacts"
                className="cta-primary"
                onClick={(e) => { e.preventDefault(); go("contacts"); }}
              >
                Get Free Consultation
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a
                href="#contacts"
                className="cta-secondary"
                onClick={(e) => { e.preventDefault(); go("contacts"); }}
              >
                Hire Me
              </a>
            </div>

            {/* ── Inline stats ── */}
            <div className="hero-stats">
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  className={`stat-item ${hovered === i ? "stat-active" : ""}`}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <span className="stat-val">{s.value}</span>
                  <span className="stat-lbl">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right column — profile image ── */}
          <div className="hero-right">
            <div className="img-wrapper">
              <div className="img-frame">
                <img src={homeLogo} alt="Shahid Hussain — AI Engineer" className="hero-img" />
              </div>
              <div className="img-glow" aria-hidden="true" />
              <div className="img-ring img-ring-a" aria-hidden="true" />
              <div className="img-ring img-ring-b" aria-hidden="true" />
            </div>

            {/* floating chips */}
            <div className="chip chip-top">
              <span className="chip-icon">⚡</span> 15+ AI APIs Shipped
            </div>
            <div className="chip chip-bot">
              <span className="chip-dot" /> Available for hire
            </div>
          </div>

        </div>
      </div>

      {/* ── social popup, divider ── */}
      <SocialPopup />
      <div className="divider" />
      <Home2 />
      <div className="divider" />

      {/* ════════════════════════════════════════════════════════
          SERVICES
      ════════════════════════════════════════════════════════ */}
      <div className="svc-section" id="services">
        <div className="svc-header">
          <span className="section-tag">› what I build</span>
          <h2 className="section-heading">
            AI-Powered Solutions<br />
            <em>Ready for Production</em>
          </h2>
          <p className="section-sub">
            From prototype to deployment — here's what I can build for your product.
          </p>
        </div>

        <div className="svc-grid">
          {services.map((s) => (
            <div className="svc-card" key={s.num}>
              <div className="svc-num">{s.num}</div>
              <div className="svc-icon">{s.icon}</div>
              <h3 className="svc-title">{s.title}</h3>
              <p className="svc-desc">{s.desc}</p>
              <div className="svc-line" />
            </div>
          ))}
        </div>

        <div className="svc-cta">
          <div>
            <p className="svc-cta-title">Not sure what you need?</p>
            <p className="svc-cta-sub">Describe your problem — I'll tell you exactly what to build.</p>
          </div>
          <a
            href="#contacts"
            className="cta-primary"
            onClick={(e) => { e.preventDefault(); go("contacts"); }}
          >
            Get Free Consultation →
          </a>
        </div>
      </div>

      {/* ── remaining sections ── */}
      <div className="divider" />
      <Projects />
      <div className="divider" />
      <About />
      <div className="divider" />
      <Certifications />
      <div className="divider" />
      <ResumeNew />
      <div className="divider" />
      <Contact />
      <div className="divider" />

      {/* ════════════════════════════════════════════════════════
          STYLES
      ════════════════════════════════════════════════════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        /* ── Reset / Root ─────────────────────────────────────── */
        .portfolio-root {
          --bg:         #07070F;
          --surface:    #0E0E1C;
          --border:     rgba(255,255,255,0.07);
          --border-hi:  rgba(255,255,255,0.14);
          --text:       #EDE9FF;
          --muted:      rgba(237,233,255,0.55);
          --accent:     #8B72FF;
          --accent-dim: rgba(139,114,255,0.18);
          --accent2:    #2DDCB4;
          --gold:       #F5C97A;
          --ff-display: 'Cormorant Garamond', Georgia, serif;
          --ff-body:    'Outfit', sans-serif;
          --ff-mono:    'JetBrains Mono', monospace;
          background: var(--bg);
          color: var(--text);
          font-family: var(--ff-body);
        }

        /* ── Cursor glow ──────────────────────────────────────── */
        .cursor-glow {
          position: fixed;
          width: 700px;
          height: 700px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(139,114,255,0.07) 0%, transparent 65%);
          transform: translate(-50%, -50%);
          pointer-events: none !important;
          z-index: 0;
          transition: left 0.6s cubic-bezier(.25,.46,.45,.94),
                      top  0.6s cubic-bezier(.25,.46,.45,.94);
          will-change: left, top;
        }

        /* ── Hero Section ─────────────────────────────────────── */
        .hero-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          padding: 130px 6vw 80px;
          overflow: hidden;
        }

        /* CSS-only background — NO canvas, NO click-blocking */
        .hero-bg {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
        }
        .bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(139,114,255,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139,114,255,0.06) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
          animation: gridDrift 30s linear infinite;
        }
        @keyframes gridDrift {
          0%   { background-position: 0 0; }
          100% { background-position: 60px 60px; }
        }
        .bg-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.55;
        }
        .bg-orb-a {
          width: 520px; height: 520px;
          background: radial-gradient(circle, rgba(139,114,255,0.25), transparent 70%);
          top: -120px; right: 8%;
          animation: orbFloat 14s ease-in-out infinite;
        }
        .bg-orb-b {
          width: 380px; height: 380px;
          background: radial-gradient(circle, rgba(45,220,180,0.15), transparent 70%);
          bottom: 10%; left: 3%;
          animation: orbFloat 18s ease-in-out infinite reverse;
        }
        .bg-orb-c {
          width: 280px; height: 280px;
          background: radial-gradient(circle, rgba(245,201,122,0.1), transparent 70%);
          bottom: 20%; right: 25%;
          animation: orbFloat 22s ease-in-out infinite 4s;
        }
        @keyframes orbFloat {
          0%, 100% { transform: translateY(0px) scale(1); }
          50%       { transform: translateY(-30px) scale(1.05); }
        }

        /* ── Hero Inner Layout ────────────────────────────────── */
        .hero-inner {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 480px;
          gap: 60px;
          align-items: center;
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.9s ease, transform 0.9s ease;
        }
        .hero-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── Hero Left ────────────────────────────────────────── */
        .hero-left {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .avail-tag {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          font-family: var(--ff-mono);
          font-size: 0.75rem;
          color: var(--accent2);
          letter-spacing: 0.08em;
          border: 1px solid rgba(45,220,180,0.25);
          background: rgba(45,220,180,0.07);
          padding: 6px 14px;
          border-radius: 40px;
          width: fit-content;
        }
        .avail-dot {
          width: 7px; height: 7px;
          background: var(--accent2);
          border-radius: 50%;
          box-shadow: 0 0 8px var(--accent2);
          animation: blink 2s ease-in-out infinite;
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }

        .hero-eyebrow {
          font-family: var(--ff-mono);
          font-size: 0.8rem;
          color: var(--muted);
          letter-spacing: 0.15em;
          margin: 0;
        }

        .hero-name {
          font-family: var(--ff-display);
          font-size: clamp(4.5rem, 8vw, 8rem);
          font-weight: 300;
          line-height: 0.95;
          letter-spacing: -0.02em;
          color: var(--text);
          margin: 0;
        }
        .name-stroke {
          /* outlined / stroke effect */
          color: transparent;
          -webkit-text-stroke: 1.5px rgba(237,233,255,0.6);
          font-style: italic;
          font-weight: 300;
        }

        .hero-bio {
          font-size: 1.1rem;
          line-height: 1.8;
          color: var(--muted);
          max-width: 520px;
          margin: 0;
        }
        .hero-bio strong {
          color: var(--text);
          font-weight: 500;
        }

        .hero-type {
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: var(--ff-mono);
          font-size: 0.9rem;
          color: var(--accent);
          min-height: 32px;
        }
        .type-prefix {
          opacity: 0.5;
        }

        /* ── CTA Buttons ──────────────────────────────────────── */
        .hero-actions {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          /* no position:relative, no z-index needed — no canvas above this */
        }

        .cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 30px;
          border-radius: 6px;
          background: var(--accent);
          color: #fff !important;
          font-family: var(--ff-body);
          font-size: 0.9rem;
          font-weight: 600;
          text-decoration: none !important;
          letter-spacing: 0.02em;
          transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
          box-shadow: 0 0 0 0 rgba(139,114,255,0);
          cursor: pointer;
        }
        .cta-primary:hover {
          background: #7A5FF0;
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(139,114,255,0.45);
          color: #fff !important;
          text-decoration: none !important;
        }
        .cta-primary svg { flex-shrink: 0; transition: transform 0.2s; }
        .cta-primary:hover svg { transform: translateX(4px); }

        .cta-secondary {
          display: inline-flex;
          align-items: center;
          padding: 14px 30px;
          border-radius: 6px;
          border: 1px solid var(--border-hi);
          color: var(--text) !important;
          font-family: var(--ff-body);
          font-size: 0.9rem;
          font-weight: 500;
          background: transparent;
          text-decoration: none !important;
          transition: border-color 0.25s, background 0.25s, transform 0.2s;
          cursor: pointer;
        }
        .cta-secondary:hover {
          border-color: var(--accent);
          background: var(--accent-dim);
          color: var(--text) !important;
          text-decoration: none !important;
          transform: translateY(-2px);
        }

        /* ── Inline Stats ─────────────────────────────────────── */
        .hero-stats {
          display: flex;
          gap: 0;
          margin-top: 8px;
          border-top: 1px solid var(--border);
          padding-top: 24px;
        }
        .stat-item {
          flex: 1;
          padding: 0 20px 0 0;
          border-right: 1px solid var(--border);
          cursor: default;
          transition: transform 0.2s;
        }
        .stat-item:first-child { padding-left: 0; }
        .stat-item:last-child { border-right: none; }
        .stat-item.stat-active { transform: translateY(-4px); }

        .stat-val {
          display: block;
          font-family: var(--ff-display);
          font-size: 2rem;
          font-weight: 600;
          color: var(--accent);
          line-height: 1;
          margin-bottom: 4px;
        }
        .stat-lbl {
          font-family: var(--ff-mono);
          font-size: 0.7rem;
          color: var(--muted);
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        /* ── Hero Right — Image ───────────────────────────────── */
        .hero-right {
          position: relative;
          display: flex;
          justify-content: center;
        }

        .img-wrapper {
          position: relative;
          width: 380px;
        }

        .img-frame {
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid var(--border-hi);
          position: relative;
          z-index: 2;
          animation: imgFloat 7s ease-in-out infinite;
        }
        @keyframes imgFloat {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-12px); }
        }

        .hero-img {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
          filter: grayscale(10%) contrast(1.05);
          transition: filter 0.4s;
        }
        .img-frame:hover .hero-img { filter: grayscale(0%) contrast(1); }

        .img-glow {
          position: absolute;
          bottom: -40px;
          left: 50%;
          transform: translateX(-50%);
          width: 60%;
          height: 80px;
          background: radial-gradient(ellipse, rgba(139,114,255,0.5), transparent 70%);
          filter: blur(24px);
          pointer-events: none;
          z-index: 1;
        }
        .img-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(139,114,255,0.15);
          pointer-events: none;
          z-index: 0;
        }
        .img-ring-a {
          width: 440px; height: 440px;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          animation: ringPulse 6s ease-in-out infinite;
        }
        .img-ring-b {
          width: 520px; height: 520px;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          animation: ringPulse 6s ease-in-out infinite 1.5s;
          border-color: rgba(45,220,180,0.08);
        }
        @keyframes ringPulse {
          0%, 100% { opacity: 0.5; transform: translate(-50%,-50%) scale(1); }
          50%       { opacity: 1;   transform: translate(-50%,-50%) scale(1.04); }
        }

        /* Floating chips */
        .chip {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(14,14,28,0.85);
          backdrop-filter: blur(12px);
          border: 1px solid var(--border-hi);
          border-radius: 40px;
          padding: 9px 16px;
          font-family: var(--ff-body);
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--text);
          white-space: nowrap;
          z-index: 5;
        }
        .chip-top {
          top: -16px;
          right: -20px;
          animation: chipFloat 5s ease-in-out infinite;
        }
        .chip-bot {
          bottom: 28px;
          left: -28px;
          animation: chipFloat 5s ease-in-out infinite 1.8s;
        }
        @keyframes chipFloat {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-6px); }
        }
        .chip-icon { font-size: 1rem; }
        .chip-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: var(--accent2);
          box-shadow: 0 0 8px var(--accent2);
          animation: blink 2s ease-in-out infinite;
        }

        /* ══ Shared section helpers ════════════════════════════ */
        .divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--border-hi), transparent);
        }

        .section-tag {
          font-family: var(--ff-mono);
          font-size: 0.78rem;
          color: var(--accent);
          letter-spacing: 0.1em;
          display: block;
          margin-bottom: 14px;
        }
        .section-heading {
          font-family: var(--ff-display);
          font-size: clamp(2.4rem, 4vw, 3.8rem);
          font-weight: 300;
          line-height: 1.1;
          color: var(--text);
          margin: 0 0 16px;
        }
        .section-heading em {
          font-style: italic;
          color: transparent;
          -webkit-text-stroke: 1px rgba(237,233,255,0.5);
        }
        .section-sub {
          font-size: 1.05rem;
          color: var(--muted);
          margin: 0;
          max-width: 500px;
          line-height: 1.7;
        }

        /* ══ Services ══════════════════════════════════════════ */
        .svc-section {
          padding: 100px 6vw;
          background: var(--bg);
          max-width: 1280px;
          margin: 0 auto;
        }

        .svc-header {
          margin-bottom: 60px;
        }

        .svc-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          border: 1px solid var(--border);
          border-radius: 16px;
          overflow: hidden;
          background: var(--border);
        }

        .svc-card {
          background: var(--bg);
          padding: 36px 28px 32px;
          position: relative;
          transition: background 0.3s;
          cursor: default;
        }
        .svc-card:hover {
          background: var(--surface);
        }
        .svc-card:hover .svc-line {
          width: 100%;
        }
        .svc-card:hover .svc-icon {
          color: var(--accent);
          transform: scale(1.15);
        }

        .svc-num {
          font-family: var(--ff-mono);
          font-size: 0.7rem;
          color: var(--muted);
          letter-spacing: 0.12em;
          margin-bottom: 20px;
        }
        .svc-icon {
          font-size: 1.6rem;
          color: var(--muted);
          transition: color 0.3s, transform 0.3s;
          display: block;
          margin-bottom: 14px;
          line-height: 1;
        }
        .svc-title {
          font-family: var(--ff-body);
          font-size: 1.05rem;
          font-weight: 600;
          color: var(--text);
          margin: 0 0 10px;
        }
        .svc-desc {
          font-size: 0.88rem;
          color: var(--muted);
          line-height: 1.75;
          margin: 0;
        }
        .svc-line {
          position: absolute;
          bottom: 0; left: 0;
          height: 2px;
          width: 0;
          background: linear-gradient(90deg, var(--accent), var(--accent2));
          transition: width 0.4s ease;
        }

        /* Services CTA */
        .svc-cta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 48px;
          padding: 36px 40px;
          border: 1px solid var(--border);
          border-radius: 14px;
          background: var(--surface);
          flex-wrap: wrap;
          gap: 24px;
        }
        .svc-cta-title {
          font-family: var(--ff-display);
          font-size: 1.6rem;
          font-weight: 400;
          color: var(--text);
          margin: 0 0 4px;
        }
        .svc-cta-sub {
          font-size: 0.92rem;
          color: var(--muted);
          margin: 0;
        }

        /* ══ Responsive ════════════════════════════════════════ */
        @media (max-width: 1024px) {
          .hero-inner {
            grid-template-columns: 1fr;
            gap: 50px;
          }
          .hero-right {
            order: -1;
          }
          .img-wrapper { width: 300px; }
          .img-ring-a { width: 340px; height: 340px; }
          .img-ring-b { width: 400px; height: 400px; }
          .chip-top { right: -10px; }
          .chip-bot { left: -10px; }
          .svc-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 680px) {
          .hero-section { padding: 100px 5vw 60px; }
          .hero-name { font-size: 4rem; }
          .hero-stats { flex-wrap: wrap; gap: 20px; }
          .stat-item { border-right: none; flex: 0 0 45%; }
          .svc-grid { grid-template-columns: 1fr; }
          .svc-cta { flex-direction: column; }
          .img-wrapper { width: 260px; }
          .chip-top { display: none; }
          .chip-bot { display: none; }
          .hero-actions { flex-direction: column; }
          .cta-primary, .cta-secondary { justify-content: center; }
        }
      `}</style>
    </section>
  );
}